/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */
import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import i18n from "./i18n";
import i18next from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { getInitialNamespaces } from "remix-i18next";
import * as Sentry from "@sentry/remix";
import { getPublicEnv } from "./helpers";

const environment = getPublicEnv("REMIX_APP_ENVIRONMENT");

// Sentry.init() runs at module evaluation time (before hydration) so no errors
// are missed. The runtime cost is ~5-15ms — negligible compared to hydration.
//
// NOTE: lazy-loading integrations (replay, profiling) via dynamic import is
// not achievable with @sentry/remix v10 — the barrel re-export from
// @sentry/browser means the full module is already in the bundle.
if (environment && environment !== "development") {
  const sentryDSN = getPublicEnv("SENTRY_DSN");

  if (!sentryDSN) {
    console.error("Missing env var: SENTRY_DSN");
  } else {
    Sentry.init({
      environment,
      dsn: sentryDSN,
      sampleRate: 1.0,
      tracesSampleRate: 1.0,
      profileSessionSampleRate: 1.0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1,
      integrations: [
        Sentry.captureConsoleIntegration({ levels: ["error"] }),
        Sentry.browserTracingIntegration({
          useEffect,
          useLocation,
          useMatches,
        }),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [window.location.origin],
        }),
      ],
      ignoreErrors: ["Can't find variable: gmo", "Can't find variable: Tapcart", "Error loading route module"],
    });
  }
}

async function hydrate() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
      detection: {
        // Here only enable 'querystring' detection, we'll detect the language only
        // server-side with remix-i18next, by using the querystring
        // we can communicate to the client the language detected server-side
        order: ["querystring", "cookie"],
        lookupQuerystring: "locale",
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>,
    );
  });
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}
